import { useState } from "react";
import { Editor, EditorState, getDefaultKeyBinding, Modifier, RichUtils } from "draft-js";
import "./RichTextEditor.css";
import { Box } from "@mui/system";
import { Circle } from "@mui/icons-material";

const RichEditor = (props) => {
  const [editorState, setEditorState] = useState(props.editorState);
  //const editorRef = useRef(null);

  //const focusEditor = () => editorRef.current.focus();

  const handleKeyCommand = (command, editorState) => {
    /*  // Get the current block count
         const blockCount = editorState.getCurrentContent().getBlockMap().size;
         // Set the maximum block count to 5
         const maxBlocks = 5;

         // Prevent the user from adding more blocks
         if (blockCount >= maxBlocks) {
           return 'handled';
         }
      */
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return true;
    }

    return false;
  };

  const mapKeyToEditorCommand = (e) => {
    if (e.keyCode === 9 /* TAB */) {
      const newEditorState = RichUtils.onTab(e, editorState, 4 /* maxDepth */);
      if (newEditorState !== editorState) {
        setEditorState(newEditorState);
      }
      return;
    }
    return getDefaultKeyBinding(e);
  };

  const toggleBlockType = (blockType) => {
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
  };

  const toggleInlineStyle = (inlineStyle) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };

  const toggleColor = (toggledColor) => {
    const selection = editorState.getSelection();
    const nextContentState = Object.keys(colorStyleMap).reduce((contentState, color) => {
      return Modifier.removeInlineStyle(contentState, selection, color);
    }, editorState.getCurrentContent());

    let nextEditorState = EditorState.push(editorState, nextContentState, "change-inline-style");

    const currentStyle = editorState.getCurrentInlineStyle();

    if (selection.isCollapsed()) {
      nextEditorState = currentStyle.reduce((state, color) => {
        return RichUtils.toggleInlineStyle(state, color);
      }, nextEditorState);
    }

    if (!currentStyle.has(toggledColor)) {
      nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, toggledColor);
    }

    onChange(nextEditorState);
  };

  let className = `RichEditor-editor`;
  var contentState = editorState.getCurrentContent();

  if (!contentState.hasText()) {
    if (contentState.getBlockMap().first().getType() !== "unstyled") {
      className += " RichEditor-hidePlaceholder";
    }
  }

  const onChange = (newEditorState) => {
    setEditorState(newEditorState);
    props.setEditorState(newEditorState);
  };

  if (props.writeStyle) {
    // Get the existing stylesheet
    const stylesheet = document.styleSheets[0];

    // Add a new rule to the stylesheet
    stylesheet.insertRule(
      `.RichEditor-editor .public-DraftEditor-content ${props.writeStyle}`,
      stylesheet.cssRules.length,
    );
  }

  /* const handleBeforeInput = (input, editorState) => {
        // Get the current block count
        const blockCount = editorState.getCurrentContent().getBlockMap().size;
        // Set the maximum block count to 5
        const maxBlocks = 5;
    
        // Prevent the user from adding more blocks
        if (blockCount >= maxBlocks) {
          return 'handled';
        }
    
        // Allow the user to add more blocks
        return 'not-handled';
    }; */

  return (
    <Box sx={props.boxStyle}>
      {props.readOnly ? (
        <Editor
          blockStyleFn={getBlockStyle}
          customStyleMap={{
            ...styleMap,
            ...colorStyleMap,
          }}
          editorState={editorState}
          readOnly={true}
          style={props.readStyle}
        />
      ) : (
        <div className="RichEditor-root">
          <BlockStyleControls editorState={editorState} onToggle={toggleBlockType} />
          <InlineStyleControls editorState={editorState} onToggle={toggleInlineStyle} />
          <ColorControls editorState={editorState} onToggle={toggleColor} />
          <Editor
            blockStyleFn={getBlockStyle}
            customStyleMap={{ ...styleMap, ...colorStyleMap }}
            editorState={editorState}
            handleKeyCommand={handleKeyCommand}
            keyBindingFn={mapKeyToEditorCommand}
            onChange={onChange}
            spellCheck={true}
            readOnly={props.readOnly}
            editorStyle={props.writeStyle}
          />
        </div>
      )}
    </Box>
  );
};

// Custom overrides for "code" style.
const styleMap = {
  CODE: {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2,
  },
};

function getBlockStyle(block) {
  switch (block.getType()) {
    case "blockquote":
      return "RichEditor-blockquote";
    default:
      return null;
  }
}

function StyleButton(props) {
  const onToggle = (e) => {
    e.preventDefault();
    props.onToggle(props.style);
  };

  let className = "RichEditor-styleButton";
  if (props.active) {
    className += " RichEditor-activeButton";
  }

  return (
    <span className={className} onMouseDown={onToggle}>
      {props.label}
    </span>
  );
}

const BLOCK_TYPES = [
  { label: "H1", style: "header-one" },
  { label: "H2", style: "header-two" },
  { label: "H3", style: "header-three" },
  { label: "H4", style: "header-four" },
  { label: "H5", style: "header-five" },
  { label: "H6", style: "header-six" },
  { label: "Blockquote", style: "blockquote" },
  { label: "UL", style: "unordered-list-item" },
  { label: "OL", style: "ordered-list-item" },
  { label: "Code Block", style: "code-block" },
];

const BlockStyleControls = (props) => {
  const { editorState } = props;
  const selection = editorState.getSelection();
  const blockType = editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getType();

  return (
    <div className="RichEditor-controls">
      {BLOCK_TYPES.map((type) => (
        <StyleButton
          key={type.label}
          active={type.style === blockType}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
};

var INLINE_STYLES = [
  { label: "Bold", style: "BOLD" },
  { label: "Italic", style: "ITALIC" },
  { label: "Underline", style: "UNDERLINE" },
  { label: "Monospace", style: "CODE" },
];

const InlineStyleControls = (props) => {
  const currentStyle = props.editorState.getCurrentInlineStyle();

  return (
    <div className="RichEditor-controls">
      {INLINE_STYLES.map((type) => (
        <StyleButton
          key={type.label}
          active={currentStyle.has(type.style)}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
};

const COLORS = [
  { label: "Red", style: "red" },
  { label: "Orange", style: "orange" },
  { label: "Yellow", style: "yellow" },
  { label: "Green", style: "green" },
  { label: "Blue", style: "blue" },
];

const colorStyleMap = {
  red: {
    color: "#E40066",
  },
  orange: {
    color: "#FB4D3D",
  },
  yellow: {
    color: "#EAC435",
  },
  green: {
    color: "#03CEA4",
  },
  blue: {
    color: "#345995",
  },
};

const ColorControls = (props) => {
  var currentStyle = props.editorState.getCurrentInlineStyle();

  const handleSelect = (color) => {
    props.onToggle(color);
  };

  const handleMouseDown = (event, color) => {
    event.preventDefault();
    handleSelect(color);
  };

  return (
    <div className="RichEditor-controls">
      {COLORS.map((type, id) => (
        <Circle
          key={id}
          sx={{
            color: colorStyleMap[type.style].color,
            opacity: !currentStyle.has(type.style) && 0.5,
            ...type.style,
            "&:hover": {
              cursor: "pointer",
            },
          }}
          label={type.label}
          onMouseDown={(e) => handleMouseDown(e, type.style)}
        />
      ))}
    </div>
  );
};

export default RichEditor;
